import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import SwapHorizOutlinedIcon from "@material-ui/icons/SwapHorizOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import MemoryIcon from "@material-ui/icons/Memory";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import UserStatusIcon from "../UserModal/statusIcon";

import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";

import {
  getKoraxModeColors,
  koraxTokens
} from "../../designSystem";

const useStyles = makeStyles(theme => {
  const colors = getKoraxModeColors(theme.mode);
  const dark = theme.mode === "dark";

  return {
    paper: {
      borderRadius: 20,
      border: `1px solid ${colors.border}`,
      overflow: "hidden",
      backgroundColor: colors.surface,

      [theme.breakpoints.down("sm")]: {
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        borderRadius: 0
      }
    },

    titleRoot: {
      padding: "20px 22px 16px",
      background:
        dark
          ? "linear-gradient(135deg,#101828,#182230)"
          : "linear-gradient(135deg,#F8FAFF,#FFFFFF)"
    },

    titleRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12
    },

    titleContent: {
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 12
    },

    titleIcon: {
      flexShrink: 0,
      width: 42,
      height: 42,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      color: "#FFFFFF",
      background: "linear-gradient(135deg,#155EEF,#0BA5EC)",
      boxShadow: "0 8px 20px rgba(21,94,239,.20)"
    },

    title: {
      color: colors.textPrimary,
      fontSize: 18,
      fontWeight: 750,
      lineHeight: 1.25,
      letterSpacing: "-0.02em"
    },

    subtitle: {
      marginTop: 3,
      color: colors.textMuted,
      fontSize: 12,
      lineHeight: 1.45
    },

    close: {
      flexShrink: 0,
      width: 34,
      height: 34,
      color: colors.textMuted
    },

    content: {
      padding: "20px 22px 22px",

      [theme.breakpoints.down("sm")]: {
        padding: "18px 16px 120px"
      }
    },

    leadCard: {
      marginBottom: 18,
      padding: "11px 12px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      borderRadius: 13,
      border: `1px solid ${colors.border}`,
      backgroundColor: dark ? "#182230" : "#F8FAFC"
    },

    leadAvatar: {
      width: 38,
      height: 38,
      backgroundColor: theme.palette.primary.main,
      fontSize: 13,
      fontWeight: 700
    },

    leadName: {
      color: colors.textPrimary,
      fontSize: 13,
      fontWeight: 700
    },

    leadMeta: {
      marginTop: 2,
      color: colors.textMuted,
      fontSize: 11
    },

    section: {
      marginTop: 17
    },

    sectionFirst: {
      marginTop: 0
    },

      destinationModes: {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 8,
        marginBottom: 20
      },

      destinationModeButton: {
        minWidth: 0,
        minHeight: 68,
        padding: "9px 7px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        backgroundColor: dark ? "#182230" : "#FFFFFF",
        color: colors.textSecondary,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: 700,
        transition: "all .15s ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          backgroundColor: dark
            ? "rgba(56,189,248,.08)"
            : "rgba(21,94,239,.045)"
        }
      },

      destinationModeButtonActive: {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        backgroundColor: dark
          ? "rgba(56,189,248,.12)"
          : koraxTokens.colors.primarySoft
      },

      destinationModeIcon: {
        fontSize: 21
      },

    labelRow: {
      marginBottom: 7,
      display: "flex",
      alignItems: "center",
      gap: 6,
      color: colors.textSecondary
    },

    labelIcon: {
      fontSize: 17,
      color: theme.palette.primary.main
    },

    label: {
      fontSize: 12,
      fontWeight: 700
    },

    helper: {
      marginTop: 6,
      color: colors.textMuted,
      fontSize: 10.5,
      lineHeight: 1.4
    },

    field: {
      "& .MuiOutlinedInput-root": {
        minHeight: 46,
        borderRadius: 12
      }
    },

    userOption: {
      width: "100%",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 9
    },

    userAvatar: {
      width: 30,
      height: 30,
      fontSize: 11,
      fontWeight: 700
    },

    userName: {
      minWidth: 0,
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 13,
      fontWeight: 600
    },

    summary: {
      marginTop: 18,
      padding: "12px 13px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      borderRadius: 13,
      border: `1px solid ${
        selectedColor(theme.mode)
      }`,
      backgroundColor:
        dark
          ? "rgba(56,189,248,.07)"
          : "rgba(21,94,239,.045)"
    },

    summaryIcon: {
      flexShrink: 0,
      width: 34,
      height: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
      color: theme.palette.primary.main,
      backgroundColor:
        dark
          ? "rgba(56,189,248,.12)"
          : koraxTokens.colors.primarySoft
    },

    summaryTitle: {
      color: colors.textPrimary,
      fontSize: 11.5,
      fontWeight: 700
    },

    summaryText: {
      marginTop: 2,
      color: colors.textMuted,
      fontSize: 10.5,
      lineHeight: 1.35
    },

    actions: {
      padding: "14px 22px 20px",
      gap: 8,
      borderTop: `1px solid ${colors.border}`,

      [theme.breakpoints.down("sm")]: {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
        backgroundColor: colors.surface,
        boxShadow: "0 -8px 24px rgba(15,23,42,.08)"
      }
    },

    cancelButton: {
      minHeight: 42,
      borderRadius: 11
    },

    submitButton: {
      minHeight: 42,
      borderRadius: 11,
      paddingLeft: 18,
      paddingRight: 18
    }
  };
});

const selectedColor = mode =>
  mode === "dark"
    ? "rgba(56,189,248,.24)"
    : "rgba(21,94,239,.16)";

const initials = name => {
  if (!name) return "?";

  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const TransferTicketModalCustom = ({
  modalOpen,
  onClose,
  ticketid,
  ticket
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const mounted = useRef(true);
  const { findAll: findAllQueues } = useQueues();

  const [allQueues, setAllQueues] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);

  const [users, setUsers] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [transferMode, setTransferMode] = useState("reception");
  const [aiPrompts, setAiPrompts] = useState([]);
  const [selectedAiPrompt, setSelectedAiPrompt] = useState("default");
  const [loadingAiPrompts, setLoadingAiPrompts] = useState(false);

  const [searchParam, setSearchParam] = useState("");
  const [msgTransfer, setMsgTransfer] = useState("");

  const [loadingQueues, setLoadingQueues] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!modalOpen) return;

    let active = true;

    const normalizeQueues = response => {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.queues)) return response.queues;
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response?.data?.queues)) {
        return response.data.queues;
      }

      return [];
    };

    const loadQueues = async () => {
      setLoadingQueues(true);

      try {
        let response;
        let list = [];

        try {
          response = await findAllQueues();
          list = normalizeQueues(response);
        } catch (hookError) {
          console.warn(
            "[Korax Transfer] erro no useQueues, tentando API direta:",
            hookError
          );
        }

        if (!list.length) {
          const directResponse = await api.get("/queue");
          list = normalizeQueues(directResponse);
        }

        const uniqueQueues = Array.from(
          new Map(
            list
              .filter(queue => queue && queue.id != null)
              .map(queue => [String(queue.id), queue])
          ).values()
        );

        console.log(
          "[Korax Transfer] filas disponíveis:",
          uniqueQueues
        );

        if (active && mounted.current) {
          setAllQueues(uniqueQueues);
          setAvailableQueues(uniqueQueues);
        }
      } catch (err) {
        console.error(
          "[Korax Transfer] erro ao carregar filas:",
          err
        );

        if (active && mounted.current) {
          setAllQueues([]);
          setAvailableQueues([]);
        }

        toastError(err);
      } finally {
        if (active && mounted.current) {
          setLoadingQueues(false);
        }
      }
    };

    loadQueues();

    return () => {
      active = false;
    };

    // findAllQueues não entra nas dependências porque o hook
    // pode devolver uma nova função a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;

    let active = true;

    const loadAiPrompts = async () => {
      setLoadingAiPrompts(true);

      try {
        const { data } = await api.get("/prompt");
        const list = Array.isArray(data?.prompts) ? data.prompts : [];

        if (active && mounted.current) {
          setAiPrompts(list);
        }
      } catch (err) {
        if (active && mounted.current) setAiPrompts([]);
        toastError(err);
      } finally {
        if (active && mounted.current) setLoadingAiPrompts(false);
      }
    };

    loadAiPrompts();

    return () => {
      active = false;
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || transferMode !== "person") return;

    let active = true;

    const loadTeamUsers = async () => {
      setLoadingUsers(true);

      try {
        let pageNumber = 1;
        let hasMore = true;
        let collectedUsers = [];

        while (hasMore && pageNumber <= 50) {
          const { data } = await api.get("/users/", {
            params: {
              searchParam: "",
              pageNumber
            }
          });

          const pageUsers =
            Array.isArray(data?.users)
              ? data.users
              : [];

          collectedUsers = [
            ...collectedUsers,
            ...pageUsers
          ];

          hasMore = Boolean(data?.hasMore);
          pageNumber += 1;
        }

        const uniqueUsers = Array.from(
          new Map(
            collectedUsers
              .filter(user => user?.id != null)
              .map(user => [
                String(user.id),
                user
              ])
          ).values()
        );

        if (active && mounted.current) {
          setUsers(uniqueUsers);
        }
      } catch (err) {
        if (active && mounted.current) {
          setUsers([]);
        }

        toastError(err);
      } finally {
        if (active && mounted.current) {
          setLoadingUsers(false);
        }
      }
    };

    loadTeamUsers();

    return () => {
      active = false;
    };
  }, [modalOpen, transferMode]);

  const selectedQueueData = useMemo(
    () =>
      allQueues.find(
        queue => String(queue.id) === String(selectedQueue)
      ),
    [allQueues, selectedQueue]
  );

  const selectedAiPromptData = useMemo(
    () =>
      selectedAiPrompt === "default"
        ? null
        : aiPrompts.find(
            prompt => String(prompt.id) === String(selectedAiPrompt)
          ) || null,
    [aiPrompts, selectedAiPrompt]
  );

  const currentWhatsappId = Number(
    ticket?.whatsappId ||
      ticket?.whatsapp?.id ||
      0
  );

  const selectedUserWhatsappBlocked =
    useMemo(() => {
      if (
        transferMode !== "person" ||
        !selectedUser ||
        !currentWhatsappId
      ) {
        return false;
      }

      // Administradores seguem com acesso irrestrito.
      if (selectedUser.profile === "admin") {
        return false;
      }

      // null/undefined preserva usuarios legados.
      if (
        !Array.isArray(
          selectedUser.whatsappIds
        )
      ) {
        return false;
      }

      const allowedWhatsappIds =
        selectedUser.whatsappIds
          .map(id => Number(id))
          .filter(
            id =>
              Number.isInteger(id) &&
              id > 0
          );

      return !allowedWhatsappIds.includes(
        currentWhatsappId
      );
    }, [
      currentWhatsappId,
      selectedUser,
      transferMode
    ]);

  const visibleTeamUsers = useMemo(() => {
    const normalizedSearch =
      searchParam.trim().toLowerCase();

    const list = users.filter(user => {
      if (!normalizedSearch) return true;

      return (
        String(user?.name || "")
          .toLowerCase()
          .includes(normalizedSearch) ||
        String(user?.email || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });

    const userIsBlocked = targetUser => {
      if (!targetUser || !currentWhatsappId) {
        return false;
      }

      if (targetUser.profile === "admin") {
        return false;
      }

      if (!Array.isArray(targetUser.whatsappIds)) {
        return false;
      }

      const allowedWhatsappIds =
        targetUser.whatsappIds
          .map(id => Number(id))
          .filter(
            id =>
              Number.isInteger(id) &&
              id > 0
          );

      return !allowedWhatsappIds.includes(
        currentWhatsappId
      );
    };

    return [...list].sort((a, b) => {
      const aBlocked = userIsBlocked(a);
      const bBlocked = userIsBlocked(b);

      if (aBlocked !== bBlocked) {
        return aBlocked ? 1 : -1;
      }

      return String(a?.name || "")
        .localeCompare(
          String(b?.name || ""),
          "pt-BR"
        );
    });
  }, [
    users,
    searchParam,
    currentWhatsappId
  ]);

  const isTeamUserBlocked = targetUser => {
    if (!targetUser || !currentWhatsappId) {
      return false;
    }

    if (targetUser.profile === "admin") {
      return false;
    }

    if (!Array.isArray(targetUser.whatsappIds)) {
      return false;
    }

    const allowedWhatsappIds =
      targetUser.whatsappIds
        .map(id => Number(id))
        .filter(
          id =>
            Number.isInteger(id) &&
            id > 0
        );

    return !allowedWhatsappIds.includes(
      currentWhatsappId
    );
  };

  const handleUserChange = (_, newUser) => {
    setSelectedUser(newUser);

    if (newUser && Array.isArray(newUser.queues)) {
      setAvailableQueues(newUser.queues);

      const currentQueueAllowed = newUser.queues.some(
        queue => String(queue.id) === String(selectedQueue)
      );

      if (!currentQueueAllowed) {
        setSelectedQueue(
          newUser.queues.length === 1
            ? newUser.queues[0].id
            : ""
        );
      }
    } else {
      setAvailableQueues(allQueues);
    }
  };

  const handleModeChange = mode => {
    setTransferMode(mode);

    setSelectedUser(null);
    setSearchParam("");
    setUsers([]);
    setSelectedAiPrompt("default");
    setAvailableQueues(allQueues);

    if (mode === "reception" || mode === "ai") {
      setSelectedQueue("");
    }
  };

  const reset = () => {
    setTransferMode("reception");
    setSelectedQueue("");
    setSelectedUser(null);
    setSelectedAiPrompt("default");
    setSearchParam("");
    setMsgTransfer("");
    setUsers([]);
    setAvailableQueues(allQueues);
  };

  const handleClose = () => {
    if (saving) return;

    reset();
    onClose();
  };

  const handleSaveTicket = async () => {
    if (!ticketid) return;

    const isAiTransfer = transferMode === "ai";

    const effectivePrompt =
      isAiTransfer && selectedAiPrompt !== "default"
        ? selectedAiPromptData
        : null;

    const effectiveQueueId =
      transferMode === "reception"
        ? null
        : transferMode === "ai"
        ? effectivePrompt?.queueId || ticket?.queueId || null
        : selectedQueue || null;

    const effectiveUser =
      transferMode === "person" ? selectedUser : null;

    if (
        (transferMode === "queue" || transferMode === "person") &&
        !effectiveQueueId
      ) return;
    if (transferMode === "person" && !effectiveUser) return;

    if (
      transferMode === "person" &&
      selectedUserWhatsappBlocked
    ) {
      return;
    }

    if (
      isAiTransfer &&
      selectedAiPrompt !== "default" &&
      !effectivePrompt
    ) return;

    setSaving(true);

    try {
      const payload = {
        userId: effectiveUser ? effectiveUser.id : null,
        // Transferir não significa que o novo atendente
        // já aceitou o atendimento.
        // Ele recebe a conversa em "Meus" como pendente/não lida.
        status: "pending",
        queueId: effectiveQueueId,
        msgTransfer: msgTransfer.trim() || null,
        isTransfered: true,
        aiActive: isAiTransfer
      };

      if (isAiTransfer) {
        payload.aiPromptId =
          selectedAiPrompt === "default"
            ? null
            : Number(selectedAiPrompt);
      }

      await api.put(`/tickets/${ticketid}`, payload);

      reset();
      onClose();
      history.push("/tickets/");
    } catch (err) {
      toastError(err);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const destinationText =
    transferMode === "reception"
      ? ticket?.queueId
        ? `${ticket?.queue?.name || "Setor atual"} · Recepção`
        : "Este atendimento não possui setor atual"
      : transferMode === "queue"
      ? selectedQueueData
        ? `${selectedQueueData.name} · aguardando atendimento`
        : "Selecione o setor de destino"
      : transferMode === "person"
      ? selectedQueueData && selectedUser
        ? `${selectedQueueData.name} · ${selectedUser.name}`
        : "Selecione uma pessoa e o setor de destino"
      : selectedAiPrompt === "default"
      ? "IA padrão deste WhatsApp"
      : selectedAiPromptData
      ? `IA · ${selectedAiPromptData.name || `Agente #${selectedAiPromptData.id}`}`
      : "Selecione o agente de IA";

  const contactName =
    ticket?.contact?.name || "Contato";

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
      classes={{
        paper: classes.paper
      }}
    >
      <DialogTitle
        disableTypography
        className={classes.titleRoot}
      >
        <div className={classes.titleRow}>
          <div className={classes.titleContent}>
            <div className={classes.titleIcon}>
              <SwapHorizOutlinedIcon />
            </div>

            <div>
              <Typography className={classes.title}>
                Transferir atendimento
              </Typography>

              <Typography className={classes.subtitle}>
                Escolha para onde esta conversa deve seguir.
              </Typography>
            </div>
          </div>

          <IconButton
            className={classes.close}
            onClick={handleClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <Divider />

      <DialogContent className={classes.content}>
        <div className={classes.leadCard}>
          <Avatar
            className={classes.leadAvatar}
            src={ticket?.contact?.urlPicture}
          >
            {initials(contactName)}
          </Avatar>

          <div>
            <div className={classes.leadName}>
              {contactName}
            </div>

            <div className={classes.leadMeta}>
              Atendimento #{ticketid}
            </div>
          </div>
        </div>

          <div className={classes.destinationModes}>
            <button
              type="button"
              className={`${classes.destinationModeButton} ${
                transferMode === "reception" ? classes.destinationModeButtonActive : ""
              }`}
              onClick={() => handleModeChange("reception")}
              disabled={saving}
            >
              <ChatBubbleOutlineIcon className={classes.destinationModeIcon} />
              Recepção
            </button>


            <button
              type="button"
              className={`${classes.destinationModeButton} ${
                transferMode === "person" ? classes.destinationModeButtonActive : ""
              }`}
              onClick={() => handleModeChange("person")}
              disabled={saving}
            >
              <PersonOutlineIcon className={classes.destinationModeIcon} />
              Pessoa
            </button>

            <button
              type="button"
              className={`${classes.destinationModeButton} ${
                transferMode === "ai" ? classes.destinationModeButtonActive : ""
              }`}
              onClick={() => handleModeChange("ai")}
              disabled={saving}
            >
              <MemoryIcon className={classes.destinationModeIcon} />
              IA
            </button>
          </div>

          {(transferMode === "queue" || transferMode === "person") && (
          <div className={classes.sectionFirst}>
            <div className={classes.labelRow}>
              <GroupOutlinedIcon className={classes.labelIcon} />
              <span className={classes.label}>
                Fila de destino
              </span>
            </div>
  
            <FormControl
              variant="outlined"
              fullWidth
              className={classes.field}
            >
              <InputLabel>Selecione uma fila</InputLabel>
  
              <Select
                value={selectedQueue}
                onChange={event =>
                  setSelectedQueue(event.target.value)
                }
                label="Selecione uma fila"
                disabled={loadingQueues || saving}
              >
                {loadingQueues ? (
                  <MenuItem disabled>
                    Carregando filas...
                  </MenuItem>
                ) : null}
  
                {!loadingQueues && availableQueues.length === 0 ? (
                  <MenuItem disabled>
                    Nenhuma fila disponível
                  </MenuItem>
                ) : null}
  
                {availableQueues.map(queue => (
                  <MenuItem
                    key={queue.id}
                    value={queue.id}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        marginRight: 9,
                        backgroundColor: queue.color || "#155EEF"
                      }}
                    />
  
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
  
            <div className={classes.helper}>
              Sem usuário selecionado, o atendimento ficará aguardando
              alguém dessa fila.
            </div>
          </div>
  
          )}

          {transferMode === "ai" && (
            <div className={classes.sectionFirst}>
              <div className={classes.labelRow}>
                <MemoryIcon className={classes.labelIcon} />
                <span className={classes.label}>Agente de IA</span>
              </div>

              <FormControl variant="outlined" fullWidth className={classes.field}>
                <InputLabel>Selecione o agente</InputLabel>
                <Select
                  value={selectedAiPrompt}
                  onChange={event => setSelectedAiPrompt(event.target.value)}
                  label="Selecione o agente"
                  disabled={loadingAiPrompts || saving}
                >
                  <MenuItem value="default">
                    IA padrão deste WhatsApp
                  </MenuItem>

                  {loadingAiPrompts ? (
                    <MenuItem disabled>Carregando agentes...</MenuItem>
                  ) : null}

                  {aiPrompts.map(prompt => (
                    <MenuItem key={prompt.id} value={String(prompt.id)}>
                      {prompt.name || `Agente #${prompt.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className={classes.helper}>
                A opção padrão usa a IA vinculada a este WhatsApp.
              </div>
            </div>
          )}

          {transferMode === "person" && (
          <div className={classes.section}>
            <div className={classes.labelRow}>
              <PersonOutlineIcon className={classes.labelIcon} />
              <span className={classes.label}>
                Escolha o responsável
              </span>
            </div>
  
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.45,
                color: "#667085",
                marginTop: -2,
                marginBottom: 10
              }}
            >
              Selecione quem deve receber este atendimento.
            </div>

            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              placeholder="Buscar colaborador..."
              value={searchParam}
              disabled={saving}
              onChange={event =>
                setSearchParam(event.target.value)
              }
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    style={{
                      marginRight: 9,
                      color: "#98A2B3",
                      fontSize: 19
                    }}
                  />
                ),
                endAdornment: loadingUsers ? (
                  <CircularProgress
                    color="inherit"
                    size={18}
                  />
                ) : null,
                style: {
                  height: 44,
                  borderRadius: 12,
                  background: "#F9FAFB",
                  fontSize: 13.5
                }
              }}
            />

            <div
              style={{
                marginTop: 10,
                border: "1px solid #EAECF0",
                borderRadius: 14,
                overflow: "hidden",
                background: "#FFFFFF",
                boxShadow:
                  "0 1px 3px rgba(16,24,40,.04)"
              }}
            >
              <div
                style={{
                  minHeight: 38,
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  borderBottom:
                    "1px solid #EAECF0",
                  background: "#FCFCFD"
                }}
              >
                <div
                  style={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    color: "#475467",
                    letterSpacing: ".01em"
                  }}
                >
                  Equipe
                </div>

                {!loadingUsers ? (
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: "#98A2B3"
                    }}
                  >
                    {visibleTeamUsers.length}{" "}
                    {visibleTeamUsers.length === 1
                      ? "pessoa"
                      : "pessoas"}
                  </div>
                ) : null}
              </div>

              <div
                style={{
                  maxHeight: 270,
                  overflowY: "auto"
                }}
              >
                {loadingUsers ? (
                  <div
                    style={{
                      padding: "24px 18px",
                      textAlign: "center",
                      color: "#667085",
                      fontSize: 12.5
                    }}
                  >
                    Carregando equipe...
                  </div>
                ) : visibleTeamUsers.length === 0 ? (
                  <div
                    style={{
                      padding: "24px 18px",
                      textAlign: "center",
                      color: "#98A2B3",
                      fontSize: 12.5
                    }}
                  >
                    Nenhum colaborador encontrado.
                  </div>
                ) : (
                  visibleTeamUsers.map(
                    (option, index) => {
                      const blocked =
                        isTeamUserBlocked(option);

                      const selected =
                        String(selectedUser?.id) ===
                        String(option?.id);

                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={
                            blocked || saving
                          }
                          onClick={() =>
                            handleUserChange(
                              null,
                              option
                            )
                          }
                          style={{
                            width: "100%",
                            minHeight: 58,
                            border: 0,
                            borderBottom:
                              index <
                              visibleTeamUsers.length - 1
                                ? "1px solid #F2F4F7"
                                : "none",
                            padding: "9px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            textAlign: "left",
                            background: blocked
                              ? "#FAFAFA"
                              : selected
                              ? "rgba(0,87,255,.055)"
                              : "#FFFFFF",
                            boxShadow: selected
                              ? "inset 3px 0 0 #0057FF"
                              : "none",
                            cursor: blocked
                              ? "not-allowed"
                              : "pointer",
                            transition:
                              "all .15s ease"
                          }}
                        >
                          <Avatar
                            className={
                              classes.userAvatar
                            }
                            style={{
                              width: 36,
                              height: 36,
                              fontSize: 11,
                              fontWeight: 800,
                              background: blocked
                                ? "#E4E7EC"
                                : selected
                                ? "#E8F0FF"
                                : "#F2F4F7",
                              color: blocked
                                ? "#98A2B3"
                                : selected
                                ? "#0057FF"
                                : "#475467"
                            }}
                          >
                            {initials(option?.name)}
                          </Avatar>

                          <div
                            style={{
                              flex: 1,
                              minWidth: 0
                            }}
                          >
                            <div
                              style={{
                                fontSize: 13,
                                lineHeight: 1.3,
                                fontWeight: 750,
                                color: blocked
                                  ? "#98A2B3"
                                  : "#101828",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow:
                                  "ellipsis"
                              }}
                            >
                              {option.name}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                fontSize: 10.8,
                                lineHeight: 1.3,
                                color: blocked
                                  ? "#B3B8C2"
                                  : "#667085",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow:
                                  "ellipsis"
                              }}
                            >
                              {option.email ||
                                "Colaborador"}
                            </div>
                          </div>

                          {blocked ? (
                            <div
                              style={{
                                flexShrink: 0,
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "#F2F4F7",
                                color: "#98A2B3",
                                fontSize: 10,
                                lineHeight: 1,
                                fontWeight: 800
                              }}
                            >
                              Sem acesso
                            </div>
                          ) : (
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                borderRadius: "50%",
                                border: selected
                                  ? "2px solid #0057FF"
                                  : "1.5px solid #D0D5DD",
                                background: selected
                                  ? "#0057FF"
                                  : "#FFFFFF",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  "center",
                                fontSize: 12,
                                fontWeight: 900
                              }}
                            >
                              {selected ? "✓" : ""}
                            </div>
                          )}
                        </button>
                      );
                    }
                  )
                )}
              </div>
            </div>

          {selectedUserWhatsappBlocked ? (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border:
                    "1px solid rgba(245,158,11,.38)",
                  background:
                    "rgba(245,158,11,.09)",
                  color: "#92400E",
                  fontSize: 12.5,
                  lineHeight: 1.45
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    marginBottom: 2
                  }}
                >
                  Este usuário não está vinculado ao WhatsApp desta conversa.
                </div>

                <div>
                  Vincule este WhatsApp ao usuário nas permissões ou selecione outro responsável.
                </div>
              </div>
            ) : null}

            </div>
  
          )}

        <div className={classes.section}>
          <div className={classes.labelRow}>
            <ChatBubbleOutlineIcon className={classes.labelIcon} />
            <span className={classes.label}>
              Mensagem interna opcional
            </span>
          </div>

          <TextField
            className={classes.field}
            value={msgTransfer}
            onChange={event =>
              setMsgTransfer(event.target.value)
            }
            variant="outlined"
            multiline
            minRows={3}
            maxRows={5}
            fullWidth
            disabled={saving}
            placeholder="Ex.: Cliente deseja informações sobre pagamento."
          />
        </div>

        <div className={classes.summary}>
          <div className={classes.summaryIcon}>
            <SwapHorizOutlinedIcon />
          </div>

          <div>
            <div className={classes.summaryTitle}>
              Destino do atendimento
            </div>

            <div className={classes.summaryText}>
              {destinationText}
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          className={classes.cancelButton}
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
        >
          Cancelar
        </Button>

        <ButtonWithSpinner
          className={classes.submitButton}
          variant="contained"
          color="primary"
          loading={saving}
            disabled={
              saving ||
              selectedUserWhatsappBlocked ||
              (transferMode === "reception"
                ? !ticket?.queueId
                : transferMode === "queue"
                ? !selectedQueue
                : transferMode === "person"
                ? !selectedQueue || !selectedUser
                : transferMode === "ai"
                ? !selectedAiPrompt
                : true)
            }
          onClick={handleSaveTicket}
          startIcon={<SwapHorizOutlinedIcon />}
        >
          Transferir
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
